/**
 * File: BrownfieldConversionPage.tsx
 * Description: SAHLD-to-Archie conversion step for brownfield projects.
 *              Streams the LLM conversion of each architecture section and
 *              persists the results so agents use them as a base during
 *              architecture generation.
 * Author: System
 * Created: 2026-03-02
 * Last Modified: 2026-03-02
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import {
  ConversionSection,
  getConversionResult,
  streamConversion,
} from '../../services/brownfieldConversionService';
import { projectService } from '../../services/projectService';
import { ProjectItem, ProjectStatus } from '../../types/project';

import { ConversionSectionModal } from './ConversionSectionModal';

// ---- Constants -------------------------------------------------------------

const SECTION_ICON: Record<string, string> = {
  business_purpose: '🎯',
  solution_summary: '📋',
  conceptual_architecture: '🏗️',
  technology_architecture: '⚙️',
  data_architecture: '🗄️',
  deployment_model: '🚀',
  security_architecture: '🔒',
  integration_architecture: '🔗',
  adr_summary: '📌',
};

// ---- Types -----------------------------------------------------------------

type SectionStatus = 'pending' | 'converting' | 'done' | 'error';

interface SectionState {
  name: string;
  label: string;
  status: SectionStatus;
  content: string;
}

// ---- Component helpers ---------------------------------------------------

/**
 * Returns a new SectionState[] with the named section set to 'converting',
 * or appends a new entry if the section is not yet tracked.
 */
function updateSectionToConverting(
  prev: SectionState[],
  eventSection: string,
  eventLabel: string,
): SectionState[] {
  const next = [...prev];
  const existing = next.find((s) => s.name === eventSection);
  if (existing) {
    existing.status = 'converting';
  } else {
    next.push({ name: eventSection, label: eventLabel, status: 'converting', content: '' });
  }
  return next;
}

/**
 * Returns a new SectionState[] with the named section set to 'done',
 * or appends a new entry if the section is not yet tracked.
 */
function updateSectionToComplete(
  prev: SectionState[],
  eventSection: string,
  eventLabel: string,
  eventContent: string,
): SectionState[] {
  const next = [...prev];
  const existing = next.find((s) => s.name === eventSection);
  if (existing) {
    existing.status = 'done';
    existing.content = eventContent;
  } else {
    next.push({ name: eventSection, label: eventLabel, status: 'done', content: eventContent });
  }
  return next;
}

// ---- Component -------------------------------------------------------------

/**
 * BrownfieldConversionPage — converts uploaded SAHLD documents into Archie
 * architecture sections suitable for use during generation.
 */
const BrownfieldConversionPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectIdNum = projectId ? Number.parseInt(projectId, 10) : 0;

  const [project, setProject] = useState<ProjectItem | null>(null);
  const [sections, setSections] = useState<SectionState[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshingSection, setRefreshingSection] = useState<string | null>(null);
  const [modalSection, setModalSection] = useState<SectionState | null>(null);

  const abortRef = useRef<boolean>(false);

  // ---- Load project & any existing conversion results --------------------

  useEffect(() => {
    if (!projectIdNum) { return; }

    const load = async () => {
      try {
        setIsLoading(true);
        const [proj, existing] = await Promise.all([
          projectService.getProject(projectIdNum),
          getConversionResult(projectIdNum).catch(() => null),
        ]);
        setProject(proj);

        if (existing && existing.sections.length > 0) {
          setSections(
            existing.sections.map((s: ConversionSection) => ({
              name: s.name,
              label: s.label,
              status: s.converted ? 'done' : 'pending',
              content: s.content,
            })),
          );
          setIsDone(existing.is_complete);
        }
      } catch {
        setError('Failed to load project data.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [projectIdNum]);

  // ---- Conversion handlers -----------------------------------------------

  const startConversion = useCallback(async () => {
    if (isConverting) { return; }
    abortRef.current = false;
    setIsConverting(true);
    setIsDone(false);
    setError(null);

    // Reset all sections to pending.
    setSections((prev) =>
      prev.length > 0
        ? prev.map((s) => ({ ...s, status: 'pending', content: '' }))
        : [],
    );

    try {
      for await (const event of streamConversion(projectIdNum)) {
        if (abortRef.current) { break; }

        if (event.type === 'section_start') {
          setSections((prev) =>
            updateSectionToConverting(prev, event.section, event.label),
          );
        } else if (event.type === 'section_complete') {
          setSections((prev) =>
            updateSectionToComplete(
              prev, event.section, event.label, event.content,
            ),
          );
        } else if (event.type === 'complete') {
          setIsDone(true);
        } else if (event.type === 'error') {
          setError(event.error);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Conversion failed.');
    } finally {
      setIsConverting(false);
    }
  }, [isConverting, projectIdNum]);

  const openModal = useCallback((section: SectionState) => {
    if (section.status !== 'done') { return; }
    setModalSection(section);
  }, []);

  const closeModal = useCallback(() => setModalSection(null), []);

  const handleModalSaved = useCallback(
    (sectionName: string, newContent: string) => {
      setSections((prev) =>
        prev.map((s) =>
          s.name === sectionName ? { ...s, content: newContent } : s,
        ),
      );
      // Update the modal's snapshot so re-save detects no drift.
      setModalSection((prev) =>
        prev?.name === sectionName
          ? { ...prev, content: newContent }
          : prev,
      );
    },
    [],
  );

  // ---- Per-section refresh -----------------------------------------------

  const refreshSection = useCallback(
    async (sectionName: string) => {
      if (isConverting || refreshingSection) { return; }
      setRefreshingSection(sectionName);
      setSections((prev) =>
        prev.map((s) =>
          s.name === sectionName ? { ...s, status: 'converting' } : s,
        ),
      );

      try {
        for await (const event of streamConversion(
          projectIdNum,
          [sectionName],
        )) {
          if (event.type === 'section_complete' &&
              event.section === sectionName) {
            setSections((prev) =>
              prev.map((s) =>
                s.name === sectionName
                  ? { ...s, status: 'done', content: event.content }
                  : s,
              ),
            );
          } else if (event.type === 'error') {
            setSections((prev) =>
              prev.map((s) =>
                s.name === sectionName ? { ...s, status: 'error' } : s,
              ),
            );
          }
        }
      } catch {
        setSections((prev) =>
          prev.map((s) =>
            s.name === sectionName ? { ...s, status: 'error' } : s,
          ),
        );
      } finally {
        setRefreshingSection(null);
      }
    },
    [isConverting, refreshingSection, projectIdNum],
  );

  // ---- Progress stats -------------------------------------------------------

  const totalSections = sections.length;
  const doneSections = sections.filter((s) => s.status === 'done').length;
  const progressPct =
    totalSections > 0 ? Math.round((doneSections / totalSections) * 100) : 0;

  // Compute button label outside JSX to avoid nested ternary
  let conversionButtonLabel: string;
  if (isConverting) {
    conversionButtonLabel = 'Converting…';
  } else if (doneSections > 0) {
    conversionButtonLabel = 'Re-run Conversion';
  } else {
    conversionButtonLabel = 'Start Conversion';
  }

  // ---- Render ---------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading project…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* ---- Stage Navigation ---- */}
      {project && (
        <StageNavigationBar
          currentStage={2.5}
          projectId={projectIdNum}
          isBrownfield={true}
          projectStatus={(() => {
            const statusMap: Record<string, ProjectStatus> = {
              'context_upload': ProjectStatus.CONTEXT_UPLOAD,
              'context_review': ProjectStatus.CONTEXT_REVIEW,
              'adr': ProjectStatus.ADR,
              'generation': ProjectStatus.GENERATION,
              'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
              'completed': ProjectStatus.COMPLETED,
            };
            return statusMap[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
          })()}
          onStageClick={(_stageId, route) => navigate(route)}
        />
      )}

      {/* ---- Header ---- */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate(`/project/${projectIdNum}/context`)}
          className="text-gray-500 hover:text-gray-900 text-sm flex items-center gap-1"
          aria-label="Back to context management"
        >
          ← Back to Context
        </button>
        <div className="h-4 w-px bg-gray-200" aria-hidden />
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            SAHLD → Archie Conversion
          </h1>
          {project && (
            <p className="text-sm text-gray-500">
              {project.title}
              {' · '}
              <span className="text-amber-600 font-medium">Brownfield</span>
            </p>
          )}
        </div>
      </div>

      {/* ---- Main content ---- */}
      <main className="flex-1 px-6 py-6 max-w-5xl mx-auto w-full">
        {/* Description */}
        <div className="shell-section rounded-lg p-5 mb-6">
          <h2 className="font-semibold text-gray-900 mb-1">
            What is this step?
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            Archie searches your uploaded SAHLD document and re-expresses each
            architecture section in its own format. During architecture
            generation these converted sections are used as the authoritative
            baseline — agents extend the existing design rather than starting
            from scratch.
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={startConversion}
            disabled={isConverting}
            className={
              isConverting
                ? 'px-5 py-2.5 rounded font-medium text-sm transition bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'px-5 py-2.5 rounded font-medium text-sm transition bg-[#0097a9] text-white hover:bg-[#00798a]'
            }
          >
            {conversionButtonLabel}
          </button>

          {isDone && (
            <button
              onClick={() => navigate(`/project/${projectIdNum}/generation`)}
              className="px-5 py-2.5 rounded font-medium text-sm bg-green-600 text-white hover:bg-green-700 transition"
            >
              Proceed to Generation →
            </button>
          )}

          {totalSections > 0 && (
            <span className="text-sm text-gray-500 ml-auto">
              {doneSections} / {totalSections} sections complete
            </span>
          )}
        </div>

        {/* Progress bar */}
        {isConverting && (
          <div className="mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[rgb(255,198,0)] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-6 rounded bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Section cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((section) => (
            <SectionCard
              key={section.name}
              section={section}
              onRefresh={() => refreshSection(section.name)}
              onOpen={() => openModal(section)}
              isRefreshing={refreshingSection === section.name}
              canRefresh={!isConverting && refreshingSection === null}
            />
          ))}
        </div>

        {/* Section edit modal */}
        {modalSection && (
          <ConversionSectionModal
            projectId={projectIdNum}
            sectionName={modalSection.name}
            sectionLabel={modalSection.label}
            sectionIcon={SECTION_ICON[modalSection.name] ?? '📄'}
            content={modalSection.content}
            onClose={closeModal}
            onSaved={handleModalSaved}
          />
        )}

        {/* Empty-state while not yet started */}
        {sections.length === 0 && !isConverting && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <span className="text-5xl mb-4">🔄</span>
            <p className="text-base font-medium">Ready to convert your SAHLD</p>
            <p className="text-sm mt-1">
              Click <strong>Start Conversion</strong> to begin.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

// ---- SectionCard component ------------------------------------------------

interface SectionCardProps {
  section: SectionState;
  onRefresh: () => void;
  onOpen: () => void;
  isRefreshing: boolean;
  canRefresh: boolean;
}

const STATUS_CONFIG: Record<
  SectionStatus,
  { label: string; colourClass: string; dotClass: string }
> = {
  pending: {
    label: 'Pending',
    colourClass: 'text-gray-500',
    dotClass: 'bg-gray-200',
  },
  converting: {
    label: 'Converting…',
    colourClass: 'text-amber-600',
    dotClass: 'bg-amber-400 animate-pulse',
  },
  done: {
    label: 'Done',
    colourClass: 'text-green-600',
    dotClass: 'bg-green-500',
  },
  error: {
    label: 'Error',
    colourClass: 'text-red-600',
    dotClass: 'bg-red-400',
  },
};

const PREVIEW_LENGTH = 350;

/**
 * Card displaying a single architecture section status and (optionally) its
 * converted content. Includes a per-section refresh button so individual
 * sections can be re-converted without re-running the entire conversion.
 */
const SectionCard: React.FC<SectionCardProps> = ({
  section,
  onRefresh,
  onOpen,
  isRefreshing,
  canRefresh,
}) => {
  const isDone = section.status === 'done';
  const isSectionConverting = section.status === 'converting';
  const cfg = STATUS_CONFIG[section.status];
  const icon = SECTION_ICON[section.name] ?? '📄';
  const hasContent = isDone && section.content.length > 0;
  const ellipsis = section.content.length > PREVIEW_LENGTH ? '…' : '';
  const preview = hasContent
    ? section.content.slice(0, PREVIEW_LENGTH) + ellipsis
    : '';

  const borderClass = isSectionConverting ? 'border-amber-300' : 'border-gray-200';
  const pointerClass = isDone
    ? 'cursor-pointer hover:border-[#0097a9] hover:shadow-sm'
    : 'cursor-default';
  const cardClassName =
    `rounded-lg border p-4 bg-white transition text-left w-full ${borderClass} ${pointerClass}`;

  const refreshBtnClass = [
    'p-1 rounded text-sm leading-none transition',
    'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#0097a9]',
    (!canRefresh || isRefreshing) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
  ].join(' ');

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    if (isDone && (e.key === 'Enter' || e.key === ' ')) { onOpen(); }
  };

  return (
    <button
      type="button"
      className={cardClassName}
      onClick={isDone ? onOpen : undefined}
      onKeyDown={onKeyDownHandler}
      title={isDone ? 'Click to view and edit' : undefined}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden>
            {icon}
          </span>
          <span className="font-medium text-sm text-gray-900">
            {section.label}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Per-section refresh button */}
          <button
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            disabled={!canRefresh || isRefreshing}
            title="Re-convert this section"
            className={refreshBtnClass}
            aria-label={`Re-convert ${section.label}`}
          >
            🔄
          </button>
          <span
            className={`h-2 w-2 rounded-full ${cfg.dotClass}`}
            aria-hidden
          />
          <span className={`text-xs font-medium ${cfg.colourClass}`}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Content preview — click card to open full editor */}
      {hasContent && (
        <div className="mt-3">
          <p className="text-xs text-gray-400 whitespace-pre-wrap break-words line-clamp-3">
            {preview}
          </p>
          <span className="inline-block text-xs text-[#0097a9] mt-1.5 font-medium">
            Click to view &amp; edit →
          </span>
        </div>
      )}
    </button>
  );
};

export default BrownfieldConversionPage;
